import React, { FC, useState, Suspense, useEffect, useDeferredValue } from 'react';
import { array, object, string, mixed, lazy as yupLazy } from 'yup';
import {
  SideSheet,
  SideSheetSize,
  SideSheetHeader,
  SideSheetClose,
  TextFieldSize,
  SideSheetFooter,
  SideSheetBody,
  Text,
  Box,
  Icon,
  IconSize,
  Select,
  FormControl,
  TextFieldAppearance,
  Checkbox,
  SideSheetContentCol,
  SideSheetSubHeader,
  useToastsDispatch,
  ToastType,
  ToastAction,
  Spinner,
  SpinnerSize,
} from '@bechtel/gds';
import {
  EllipsisPlacement,
  TextWithEllipsis,
  SectionHeader,
  ErrorBoundary,
  SuspenseLoader,
  ErrorRetry,
  useName,
  useAppUser,
} from '@bechtel/client-common';
import { Formik, FormikProps, FieldArray } from 'formik';
import { EditRequestProps, FormikState } from './types';
import { Plural, t, Trans } from '@lingui/macro';
import {
  AddButtonStyled,
  AssociatedWeldStyled,
  MaterialContainerStyled,
  FooterButtonStyled,
  DeleteButtonStyled,
  HighLightCardStyled,
  SaveButtonStyled,
  SearchFieldComponentStyled,
  SearchFieldStyled,
  SkewedCardDarkStyled,
  SideSheetContentColStyled,
} from './styled';
import {
  WeldTagItem,
  StencilSelector,
  useRodFillerMaterialQuery,
  useWeldsByWeldProcedureQuery,
  useCrewMemberWithSupervisor,
  useRodRoomPermissions,
} from '@bechtel/client-weld-common';
import {
  Weld,
  RodFillerMaterial,
  UpdateRodRequestInput,
  CrewMemberList,
  CrewMemberDiscipline,
  CommodityFilterByTerm,
  FilterOperator,
  CommodityComplexFilterInput,
  Project,
  RodDiameter,
  PersonaType,
  CrewMemberSortByTerm,
} from '@bechtel/types';
import { UpdateRodRequestError, UpdateRodRequestErrorType } from '../error';
import { NetworkStatus, useLazyQuery } from '@apollo/client';
import { GET_PROJECT_COMMODITY_TYPES_BY_ID } from '@bechtel/client-gql';
import { find, findIndex, get, isNull, map, some, toLower, flatMap } from 'lodash';
import { useUpdateRodRequest } from '~/hooks';
import { CircleStyled } from '~/components/organisms/rod-request-details/welds/styled';

export const AssociatedWeld = ({ item, onAdd }) => {
  return (
    <Box
      px={{ sm: '500' }}
      py={{ sm: '400' }}
      justifyContent={{ sm: 'space-between' }}
      flexGrow={{ sm: 1 }}
      alignItems={{ sm: 'center' }}
      css={{ cursor: 'pointer' }}
      onClick={() => {
        onAdd(item.id);
      }}
    >
      <Box flexDirection={{ sm: 'column' }} mr={{ sm: '500' }}>
        <TextWithEllipsis ellipsisPlacement={EllipsisPlacement.END} size="500" transform="uppercase" color="grey.1000">
          {item.tag}
        </TextWithEllipsis>
        <Box mt={{ sm: '300' }} alignItems={{ sm: 'center' }}>
          {item?.pipeMaterial && (
            <>
              <Text size="300" color="grey.700">
                {item.pipeMaterial}
              </Text>
              <CircleStyled />
            </>
          )}
          {item?.type?.name && (
            <>
              <Text size="300" color="grey.700">
                {item.type.name}
              </Text>
              <CircleStyled />
            </>
          )}
          {item?.pipeDiameter && item?.pipeDiameter > 0 && (
            <>
              <Text size="300" color="grey.700">
                {`${item?.pipeDiameter}"`}&nbsp;
                <Trans>Diameter</Trans>
              </Text>
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
};

const UpdateRodRequestToast = (requestNumber: string) => {
  return (
    <Box p={{ sm: '500' }} flexDirection={{ sm: 'column' }}>
      <Text family="graphik" weight="600" size="500">
        {t`Rod Request with request number ${requestNumber} successfully updated.`}
      </Text>
    </Box>
  );
};

const FillerMaterialItem = ({ item }: { item: RodFillerMaterial }) => {
  return <Text size="400">{item?.label}</Text>;
};

const validationSchema = object().shape({
  fillerMaterials: array()
    .of(
      object().shape({
        fillerMaterial: yupLazy((value) => {
          if (value) {
            return object().shape({
              id: string().required(t`Filler material is mandatory`),
            });
          }
          return mixed().required(t`Filler material is mandatory`);
        }),
        diameters: array()
          .required()
          .min(1, t`Select at least one rod size`),
      })
    )
    .required()
    .min(1, t`Select at least one filler material`),
  stencils: array()
    .required()
    .min(1, t`Select at least 1 stencil`),
  welds: array()
    .required()
    .min(1, t`Select at least 1 weld`),
});
export const EditRodRequest: FC<EditRequestProps> = ({ rodRequest, onClose }) => {
  const [initialValues, setInitialValues] = useState<FormikState>();
  const { user } = useAppUser();
  const { getFullName } = useName();
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [actionCompleted, setActionCompleted] = useState<boolean>(false);
  const [editMaterial, setEditMaterial] = useState(false);
  const [materialIndex, setMaterialIndex] = useState<number | null>();
  const [editAssociatedWeld, setEditAssociatedWeld] = useState(false);
  const [updateRodRequestError, setUpdateRodRequestError] = useState<UpdateRodRequestErrorType | null>(null);
  const debouncedSearchQuery = useDeferredValue(searchQuery?.trim());
  const dispatchToast = useToastsDispatch();
  const { canUpdateRodRequest } = useRodRoomPermissions(
    rodRequest?.welds && rodRequest?.welds.length > 0 ? rodRequest.welds[0].commodityAssignees : []
  );
  const [totalWelds, setTotalWelds] = useState(0);
  const weldsLimit = 20;
  const { loading: rodFillerMaterialLoading, data: rodFillerMaterialData } = useRodFillerMaterialQuery();
  const rodFillerMaterialList: RodFillerMaterial[] = get(rodFillerMaterialData, 'rodFillerMaterials.items', []);
  const [
    getWelders,
    {
      data: crewMemberData,
      error: crewMemberError,
      loading: crewMemberLoading,
      refetch: crewMemberRefetch,
      networkStatus: crewMemberNetworkStatus,
      called: crewMemberCalled,
    },
  ] = useCrewMemberWithSupervisor();
  const welderList = get(crewMemberData, 'crewMembers.items', []) as CrewMemberList['items'];
  const [
    loadWelds,
    { loading: weldsLoading, data: weldsData, error: weldsError, refetch: weldsRefetch, fetchMore: weldsFetchMore },
  ] = useWeldsByWeldProcedureQuery(true);
  const weldList: Weld[] = map(get(weldsData, 'commodities.items', []), 'weld');
  const weldsCount = get(weldsData, 'commodities.total');
  useEffect(() => {
    if(weldsCount && weldsCount !== totalWelds) {
      setTotalWelds(weldsCount)
    }
}, [weldsCount]);
  const totalPages = totalWelds ? Math.ceil(totalWelds / weldsLimit) : 0;
  const weldListCursor = get(weldsData, 'commodities.cursor', 1);
  const [
    updateRodRequest,
    { loading: mutationLoading, error: mutationError, data: mutationData },
  ] = useUpdateRodRequest(() => {
    setActionCompleted(true);
  });

  // TODO: remove below code to get the weldtypeid once BE starts providing welds by weld procedure
  const [getProject, { data: projectData, called: projectCalled }] = useLazyQuery<{
    projectsByIds: Project[];
  }>(GET_PROJECT_COMMODITY_TYPES_BY_ID, {
    notifyOnNetworkStatusChange: true,
  });
  const projectDetailArr: Project[] = get(projectData, 'projectsByIds', []);
  const projectDetail = projectDetailArr.length > 0 ? projectDetailArr[0] : null;
  const weldTypeId = find(projectDetail?.projectConfiguration.projectCommodityTypes, (item) => {
    return toLower(item.name) === 'weld';
  })?.id;

  useEffect(() => {
    if (!rodRequest) return;
    const initialFormikValues: FormikState = {
      weldProcedureId: rodRequest?.weldProcedure?.id,
      fillerMaterials: rodRequest?.fillerMaterials ? [...rodRequest.fillerMaterials] : [],
      rodDiameters: editMaterial ? rodRequest?.fillerMaterials[materialIndex]?.diameters : [],
      stencils: rodRequest?.stencils ? map(rodRequest.stencils, (item) => item.stencil) : [],
      welds: rodRequest?.welds ? rodRequest.welds : [],
      selectedWelds: [],
      weldList: [],
    };
    setInitialValues({ ...initialFormikValues });
  }, [rodRequest]);

  const handleScroll = (e) => {
    if (
      e.target.scrollTop + e.target.clientHeight + 200 >= e.target.scrollHeight &&
      weldListCursor <= totalPages &&
      !weldsLoading
    ) {
      weldsFetchMore({
        variables: {
          paging: {
            cursor: weldListCursor.toString(),
            limit: weldsLimit,
          },
        },
      });
    }
  };

  // TODO: remove below useEffect to call getProject once BE starts providing welds by weld procedure
  useEffect(() => {
    if (!user || !user.project || projectCalled) return;
    getProject({
      variables: {
        ids: [user?.project?.id],
      },
    });
  }, [getProject, projectCalled, user]);

  useEffect(() => {
    if (!actionCompleted) return;
    const error: UpdateRodRequestErrorType = get(mutationData, 'updateRodRequest.error', '') || mutationError;
    if (!error) {
      const toastAction: ToastAction = {
        type: 'ADD_TOAST',
        payload: {
          toastType: ToastType.INFO,
          content: () => UpdateRodRequestToast(get(mutationData, 'updateRodRequest.rodRequest.requestNumber', '')),
          autoDismissTimeout: 5000,
        },
      };
      dispatchToast(toastAction);
      onClose();
    } else {
      setUpdateRodRequestError(error);
    }
    setActionCompleted(false);
  }, [actionCompleted, dispatchToast, mutationData, mutationError, onClose]);

  useEffect(() => {
    if (!rodRequest || !rodRequest.stencils || rodRequest.stencils.length === 0 || crewMemberCalled) return;
    getWelders({
      variables: {
        input: {
          discipline: CrewMemberDiscipline.WELDING,
          sortBy: CrewMemberSortByTerm.WELDER_STENCIL,
          supervisorId: rodRequest.stencils[0]?.stencil?.supervisor?.id,
        },
        paging: {
          cursor: '1',
          limit: 20,
        },
      },
    });
  }, [crewMemberCalled, getWelders, rodRequest, rodRequest.stencils]);

  useEffect(() => {
    if (
      !rodRequest ||
      !rodRequest.weldProcedure ||
      !weldTypeId ||
      (debouncedSearchQuery.length > 0 && debouncedSearchQuery.length < 3) ||
      !editAssociatedWeld
    )
      return;

    const weldComplexFilter: CommodityComplexFilterInput = {
      and: [
        //TODO: uncomment below code once BE starts providing data based on weld procedure
        // {
        //   term: CommodityFilterByTerm.WELD_PROCEDURE_ID,
        //   operator: FilterOperator.EQUALS,
        //   value: rodRequest.weldProcedure.id,
        // },
        // TODO: remove COMMODITY_TYPE_ID filter once BE starts providing welds by weld procedure
        {
          term: CommodityFilterByTerm.COMMODITY_TYPE_ID,
          value: weldTypeId,
          operator: FilterOperator.EQUALS,
        },
      ],
    };

    if (debouncedSearchQuery)
      weldComplexFilter.and?.push({
        term: CommodityFilterByTerm.TAG_NUMBER,
        value: debouncedSearchQuery,
        operator: FilterOperator.CONTAINS,
      });
    loadWelds({
      variables: {
        input: {
          complexFilter: weldComplexFilter,
        },
        paging: {
          cursor: '1',
          limit: weldsLimit,
        },
      },
    });
  }, [rodRequest.weldProcedure, weldTypeId, debouncedSearchQuery, rodRequest, loadWelds, editAssociatedWeld]);

  return (
    <SideSheet
      visible
      size={editMaterial || editAssociatedWeld ? SideSheetSize.MEDIUM : SideSheetSize.DEFAULT}
      data-testid="edit-rod-request"
    >
      <SideSheetHeader title={t`Edit Request`} data-testid="side-sheet-header">
        <SideSheetClose onClick={() => onClose()} />
      </SideSheetHeader>
      <SideSheetSubHeader>
        <Box
          flexDirection={{
            sm: 'column',
          }}
        >
          <Box mb={{ sm: '200' }}>
            <Text color="grey.500" size="300">
              <Trans>Weld Procedure</Trans>
            </Text>
          </Box>
          <Text color="grey.1000" size="400" weight="600">
            {rodRequest?.weldProcedure?.procedure}
          </Text>
        </Box>
      </SideSheetSubHeader>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        enableReinitialize={true}
        onSubmit={(values) => {
          const updateRodRequestInput: UpdateRodRequestInput = {
            fillerMaterialDiameterIds: map(flatMap(values?.fillerMaterials, 'diameters'), 'id'),
            rodRequestId: rodRequest.id,
            stencilIds: map(values?.stencils, 'id'),
            weldIds: map(values?.welds, 'id'),
            rodRoomId: rodRequest?.rodRoom?.id,
            weldProcedureId: rodRequest?.weldProcedure?.id,
            pickUpTime: rodRequest?.pickUpTime,
          };
          updateRodRequest({
            variables: {
              input: updateRodRequestInput,
            },
          });
        }}
      >
        {(props: FormikProps<FormikState>) => {
          const { values, dirty, isValid, handleSubmit, setFieldValue } = props;
          return (
            <>
              <SideSheetBody>
                <SideSheetContentCol
                  style={{
                    boxShadow:
                      '0 1px 5px 0 rgba(0, 0, 0, 0.2), 0 3px 1px -2px rgba(0, 0, 0, 0.12), 0 2px 2px 0 rgba(0, 0, 0, 0.14)',
                  }}
                >
                  <Box flexDirection={{ sm: 'column' }} flexGrow={{ sm: '1' }} pb={{ sm: '800' }}>
                    <FieldArray name="fillerMaterials">
                      {({ push }) => {
                        return (
                          <Box data-testid="filler-material" flexDirection={{ sm: 'column' }} flexGrow={{ sm: '1' }}>
                            <Box flexDirection={{ sm: 'column' }}>
                              <SectionHeader title={t`Filler Material`} />
                              {map(values?.fillerMaterials, (materialItem, index) => (
                                <HighLightCardStyled
                                  isActive={index == materialIndex}
                                  key={materialItem?.fillerMaterial.id}
                                >
                                  <MaterialContainerStyled
                                    flexGrow={{ sm: 1 }}
                                    isActive={index == materialIndex}
                                    onClick={() => {
                                      setMaterialIndex(index);
                                      setFieldValue('rodDiameters', values?.fillerMaterials[index]?.diameters);
                                      setEditMaterial(true);
                                      setEditAssociatedWeld(false);
                                    }}
                                  >
                                    <Box p={{ sm: '450' }} alignItems={{ sm: 'center' }}>
                                      <Text size="300" family="graphik">
                                        {materialItem.fillerMaterial.label}
                                      </Text>
                                    </Box>
                                    <Box justifyContent={{ sm: 'center' }}>
                                      {map(materialItem.diameters, (rodItem, index) => {
                                        return (
                                          <Box alignItems={{ sm: 'center' }} key={index}>
                                            {Number(index) % 2 != 0 && (
                                              <SkewedCardDarkStyled>
                                                <Text
                                                  size="300"
                                                  family="graphik"
                                                  style={{
                                                    zIndex: 1,
                                                  }}
                                                >
                                                  {rodItem?.label}
                                                </Text>
                                              </SkewedCardDarkStyled>
                                            )}
                                            {Number(index) % 2 == 0 && (
                                              <Box pr={{ sm: '450' }}>
                                                <Text size="300" family="graphik" style={{ zIndex: 1 }}>
                                                  {rodItem?.label}
                                                </Text>
                                              </Box>
                                            )}
                                          </Box>
                                        );
                                      })}
                                    </Box>
                                  </MaterialContainerStyled>
                                </HighLightCardStyled>
                              ))}
                            </Box>

                            <Box p={{ sm: '500' }}>
                              <AddButtonStyled
                                data-testid="add-filler-material-button"
                                onClick={() => {
                                  push({
                                    fillerMaterial: {
                                      id: '',
                                      label: '',
                                    },
                                    diameters: [],
                                  });
                                  setEditMaterial(true);
                                  setMaterialIndex(values?.fillerMaterials?.length);
                                  setEditAssociatedWeld(false);
                                }}
                              >
                                <Box mr={{ sm: '400' }}>
                                  <Icon icon="plus" fillColor="bechtel.blue.600" size={IconSize.SMALL} />
                                </Box>
                                <Trans>Add New</Trans>
                              </AddButtonStyled>
                            </Box>
                          </Box>
                        );
                      }}
                    </FieldArray>
                    <Box flexDirection={{ sm: 'column' }}>
                      <SectionHeader title={t`Welders`} />
                      {!crewMemberLoading && crewMemberError && (
                        <div data-testid="welder-error">
                          <ErrorRetry refetch={crewMemberRefetch}>
                            <Trans>There was a problem loading Welders</Trans>
                          </ErrorRetry>
                        </div>
                      )}
                      {!crewMemberError && (
                        <Box mt={{ sm: '500' }} mb={{ sm: '400' }} mx={{ sm: '450' }} data-testid="stencil-section">
                          <StencilSelector
                            onSelectedItemsChange={(selectedItems) => {
                              getWelders({
                                variables: {
                                  input: {
                                    discipline: CrewMemberDiscipline.WELDING,
                                    sortBy: CrewMemberSortByTerm.WELDER_STENCIL,
                                    supervisorId:
                                      selectedItems && selectedItems.length > 0 ? selectedItems[0]?.supervisor?.id : '',
                                  },
                                  paging: {
                                    cursor: '1',
                                    limit: 20,
                                  },
                                },
                              });
                            }}
                            fieldName="stencils"
                            items={welderList}
                            loading={crewMemberLoading || crewMemberNetworkStatus === NetworkStatus.refetch}
                            hideDropDownIcon={true}
                          />
                        </Box>
                      )}

                      {values?.stencils?.length > 0 && (
                        <Box mx={{ sm: '450' }} mt={{ sm: '100' }} mb={{ sm: '500' }}>
                          <Text size="300" family="graphik">
                            <Plural
                              value={values?.stencils?.length}
                              one={`${values?.stencils?.length} Welder selected is under`}
                              other={`${values?.stencils?.length} Welders selected are under`}
                            />
                          </Text>
                          &nbsp;
                          <Text size="300" family="graphik" weight="600">
                            {getFullName(
                              values?.stencils[0]?.supervisor?.firstName,
                              values?.stencils[0]?.supervisor?.lastName
                            )}
                          </Text>
                        </Box>
                      )}
                    </Box>

                    <SectionHeader title={t`Associated Welds`} />
                    <Box flexDirection={{ sm: 'column' }} flexGrow={{ sm: '1' }} data-testid="associated-welds">
                      <FieldArray name="welds">
                        {({ remove }) => {
                          return map(values?.welds, (weld: Weld, index) => {
                            return (
                              <Box
                                key={weld.id}
                                py={{
                                  sm: '400',
                                }}
                              >
                                <WeldTagItem
                                  item={weld}
                                  showDelete={true}
                                  onDelete={() => {
                                    remove(index);
                                    if (!editMaterial) return;
                                    setEditMaterial(false);
                                  }}
                                />
                              </Box>
                            );
                          });
                        }}
                      </FieldArray>
                      <Box p={{ sm: '500' }}>
                        <AddButtonStyled
                          onClick={() => {
                            setEditAssociatedWeld(true);
                            setEditMaterial(false);
                          }}
                        >
                          <Box mr={{ sm: '400' }}>
                            <Icon icon="plus" fillColor="bechtel.blue.600" size={IconSize.SMALL} />
                          </Box>
                          <Trans>Add New</Trans>
                        </AddButtonStyled>
                      </Box>
                    </Box>
                  </Box>
                </SideSheetContentCol>
                {(editMaterial || editAssociatedWeld) && (
                  <SideSheetContentColStyled>
                    {editMaterial && (
                      <Box data-testid="add-new-material" flexDirection={{ sm: 'column' }}>
                        <Box
                          p={{ sm: '500' }}
                          css={`
                            background: ${({ theme }) => theme.colors.grey[200]};
                          `}
                        >
                          <Text weight="600" family="graphik" size="400" color="grey.1000">
                            {!values?.fillerMaterials[materialIndex]?.fillerMaterial?.label
                              ? t`Add New Material`
                              : t`Modify Sizes`}
                          </Text>
                        </Box>
                        <Box p={{ sm: '500' }} flexWrap={{ sm: 'wrap' }} justifyContent={{ sm: 'end' }}>
                          <Box
                            flexDirection={{ sm: 'column' }}
                            css={`
                              border-bottom: 1px solid ${({ theme }) => theme.colors.grey[300]};
                              width: 100%;
                            `}
                          >
                            <Box mb={{ sm: '500' }}>
                              <FormControl label={t`Filler Material`} style={{ flexGrow: 1 }}>
                                <Select
                                  selectedItem={find(rodFillerMaterialList, {
                                    id: values?.fillerMaterials[materialIndex]?.fillerMaterial?.id,
                                  })}
                                  placeholder={t`Please Select`}
                                  size={TextFieldSize.LARGE}
                                  appearance={TextFieldAppearance.NORMAL}
                                  items={rodFillerMaterialList}
                                  itemComponent={FillerMaterialItem}
                                  selectedComponent={FillerMaterialItem}
                                  loading={rodFillerMaterialLoading}
                                  onSelectedItemChange={(changes: any) => {
                                    const itemSelected = some(values?.fillerMaterials, (item) => {
                                      return item?.fillerMaterial.id === changes.selectedItem.id;
                                    });
                                    if (itemSelected) return;
                                    setFieldValue('rodDiameters', []);
                                    setFieldValue(`fillerMaterials[${materialIndex}].diameters`, []);
                                    setFieldValue(
                                      `fillerMaterials[${materialIndex}].fillerMaterial`,
                                      changes.selectedItem
                                    );
                                  }}
                                />
                              </FormControl>
                            </Box>

                            {values?.fillerMaterials[materialIndex]?.fillerMaterial?.label && (
                              <FieldArray name="rodDiameters">
                                {({ push, remove }) => {
                                  return map(
                                    map(
                                      values?.fillerMaterials[materialIndex]?.fillerMaterial?.rodFillerMaterialDetails,
                                      'diameter'
                                    ),
                                    (rodDiameter: RodDiameter) => {
                                      const itemIndex = findIndex(values?.rodDiameters, (item) => {
                                        return item?.id === rodDiameter.id;
                                      });
                                      const itemSelected = some(values?.rodDiameters, (item) => {
                                        return item.id === rodDiameter.id;
                                      });
                                      return (
                                        <Box
                                          pb={{ sm: '500' }}
                                          pt={{ sm: '300' }}
                                          alignItems={{ sm: 'center' }}
                                          key={
                                            rodDiameter.label +
                                            values?.fillerMaterials[materialIndex]?.fillerMaterial?.label
                                          }
                                          onClick={() => {
                                            if (itemIndex !== -1) {
                                              remove(itemIndex);
                                            } else {
                                              push(rodDiameter);
                                            }
                                          }}
                                        >
                                          <Box mr={{ sm: '400' }}>
                                            <Checkbox checked={itemSelected} readOnly />
                                          </Box>
                                          <Text size="300" color="bechtel.blue.800" family="graphik">
                                            {rodDiameter.label}
                                          </Text>
                                        </Box>
                                      );
                                    }
                                  );
                                }}
                              </FieldArray>
                            )}
                          </Box>

                          <Box justifyContent={{ sm: 'flex-end' }} mt={{ sm: '500' }}>
                            {values?.fillerMaterials.length > 0 && (
                              <DeleteButtonStyled
                                onClick={() => {
                                  if (!isNull(materialIndex)) {
                                    values?.fillerMaterials.splice(materialIndex, 1);
                                    setFieldValue('fillerMaterials', values?.fillerMaterials);
                                    setMaterialIndex(null);
                                    setEditMaterial(false);
                                  }
                                }}
                              >
                                <Box p={{ sm: '400' }}>
                                  <Icon fillColor="red.500" icon="trash-alt" size={IconSize.MEDIUM} />
                                </Box>
                                <Trans>Delete</Trans>
                              </DeleteButtonStyled>
                            )}
                            {values?.fillerMaterials[materialIndex] &&
                              values?.fillerMaterials[materialIndex]?.fillerMaterial &&
                              values?.fillerMaterials[materialIndex]?.fillerMaterial?.label && (
                                <SaveButtonStyled
                                  onClick={() => {
                                    setEditMaterial(false);
                                    setFieldValue(`fillerMaterials[${materialIndex}].diameters`, values?.rodDiameters);
                                  }}
                                >
                                  <Trans>Save</Trans>
                                </SaveButtonStyled>
                              )}
                          </Box>
                        </Box>
                      </Box>
                    )}
                    {editAssociatedWeld && (
                      <>
                        <Box flexDirection={{ sm: 'column' }} css={{ height: '100%' }}>
                          <Box
                            flexDirection={{
                              sm: 'column',
                            }}
                            css={`
                              top: 0;
                              left: 0;
                              right: 0;
                              width: 100%;
                              background: ${({ theme }) => theme.colors.white};
                            `}
                          >
                            <SectionHeader title={t`Add Associated Weld`} />
                            <SearchFieldStyled data-testid="search-field">
                              <SearchFieldComponentStyled
                                size={TextFieldSize.LARGE}
                                iconSize={IconSize.MEDIUM}
                                placeholder={t`Search`}
                                type="search"
                                onChange={(e) => setSearchQuery(e.target.value)}
                                value={searchQuery}
                              />
                            </SearchFieldStyled>
                          </Box>

                          <Box
                            onScroll={(e) => {
                              handleScroll(e);
                            }}
                            flexDirection={{ sm: 'column' }}
                            css={{
                              overflowY: 'auto',
                              height: '100%',
                            }}
                            ml={{
                              sm: '100',
                            }}
                            pb={{ sm: '700' }}
                          >
                            <FieldArray name="selectedWelds">
                              {({ push, remove }) => {
                                return (
                                  <>
                                    {map(weldList, (weldItem: Weld) => {
                                      return (
                                        <AssociatedWeldStyled
                                          isDisable={
                                            findIndex(values?.welds, (item: Weld) => {
                                              return item?.id === weldItem.id;
                                            }) !== -1
                                          }
                                          isSelected={
                                            findIndex(values?.selectedWelds, (item: Weld) => {
                                              return item?.id === weldItem.id;
                                            }) !== -1
                                          }
                                          key={weldItem.id}
                                        >
                                          <AssociatedWeld
                                            item={weldItem}
                                            onAdd={(weldId) => {
                                              const selectedWelds = values?.selectedWelds
                                                ? [...values.selectedWelds]
                                                : [];
                                              const weldIdIndex = findIndex(selectedWelds, (item: Weld) => {
                                                return item.id === weldId;
                                              });
                                              if (selectedWelds?.length > 0 && weldIdIndex !== -1) {
                                                remove(weldIdIndex);
                                              } else {
                                                push(weldItem);
                                              }
                                            }}
                                          />
                                        </AssociatedWeldStyled>
                                      );
                                    })}
                                  </>
                                );
                              }}
                            </FieldArray>
                            {weldsLoading && (
                              <Box alignItems={{ sm: 'center' }} justifyContent={{ sm: 'center' }} flexGrow={{ sm: 1 }}>
                                <Spinner size={SpinnerSize.MEDIUM} />
                              </Box>
                            )}
                            {!weldsLoading && weldsError && (
                              <Box alignItems={{ sm: 'center' }} justifyContent={{ sm: 'center' }} flexGrow={{ sm: 1 }}>
                                <ErrorRetry refetch={weldsRefetch}>
                                  <Trans>There was a problem loading welds</Trans>
                                </ErrorRetry>
                              </Box>
                            )}
                          </Box>
                          <Box
                            css={`
                              position: sticky;
                              bottom: ${({ theme }) => theme.spacings[700]};
                              background: ${({ theme }) => theme.colors.white};
                              width: 100%;
                            `}
                            justifyContent={{
                              sm: 'end',
                            }}
                            ml={{
                              sm: '100',
                            }}
                          >
                            <Box
                              alignSelf={{ sm: 'end' }}
                              css={`
                                width: 30%;
                              `}
                              p={{ sm: '550' }}
                            >
                              <SaveButtonStyled
                                onClick={() => {
                                  setFieldValue('welds', [...values.welds, ...values.selectedWelds]);
                                  setFieldValue('selectedWelds', []);
                                  setEditAssociatedWeld(false);
                                  setEditMaterial(false);
                                }}
                              >
                                <Trans>Done</Trans>
                              </SaveButtonStyled>
                            </Box>
                          </Box>
                        </Box>
                      </>
                    )}
                  </SideSheetContentColStyled>
                )}
              </SideSheetBody>
              <SideSheetFooter>
                <Box
                  p={{ sm: '500' }}
                  css={`
                    background: ${({ theme }) => theme.colors.bechtel.blue[800]};
                    position: absolute;
                    bottom: 0;
                    right: 0;
                    width: 100%;
                  `}
                  justifyContent={{
                    sm: 'flex-end',
                  }}
                >
                  <FooterButtonStyled onClick={onClose} data-testid="request-cancel" disabled={mutationLoading}>
                    <Trans>Cancel</Trans>
                  </FooterButtonStyled>

                  {canUpdateRodRequest && (
                    <FooterButtonStyled
                      disabled={mutationLoading || !dirty || !isValid}
                      onClick={() => handleSubmit()}
                      data-testid="request-update"
                    >
                      {mutationLoading ? <Spinner /> : <Trans>Update</Trans>}
                    </FooterButtonStyled>
                  )}
                </Box>
              </SideSheetFooter>
            </>
          );
        }}
      </Formik>
      {updateRodRequestError && (
        <ErrorBoundary>
          <Suspense fallback={<SuspenseLoader></SuspenseLoader>}>
            <UpdateRodRequestError error={updateRodRequestError} close={() => setUpdateRodRequestError(null)} />
          </Suspense>
        </ErrorBoundary>
      )}
    </SideSheet>
  );
};
